import React, { useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { apiGet, apiPost } from '../utils/api';
import DiscordIcon from './DiscordIcon';

// Validation functions
const validatePlayerTag = (tag) => {
  const pattern = /^#[A-Z0-9]{3,9}$/;
  return pattern.test(tag);
};

const LoadingSpinner = () => (
  <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
);

/**
 * OnboardingModal supports two modes:
 *  - 'post-login'  : user already signed in but has no clan linked
 *                    (auto-popped; closing = logout)
 *  - 'pre-login'   : user fills form first, final step = sign in with Discord
 *                    (triggered by "Request Access" button)
 */
export default function OnboardingModal({ clans, onComplete, onClose, mode = 'post-login' }) {
  const { t } = useTranslation();
  const { refreshProfile, logout, loginWithDiscord, isAuthenticated } = useAuth();

  // pre-login flow has 3 steps; post-login has 2
  const totalSteps = mode === 'pre-login' ? 3 : 2;
  const [step, setStep] = useState(1);
  const [playerTag, setPlayerTag] = useState('');
  const [clanTag, setClanTag] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [clanName, setClanName] = useState('');
  const [clanBadgeUrl, setClanBadgeUrl] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [validationStatus, setValidationStatus] = useState(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  // Map backend error codes to localized messages
  const translateError = useCallback((error) => {
    const errorMap = {
      'invalid_player_tag': t('error_invalid_player_tag'),
      'clan_tag_required': t('error_clan_tag_required'),
      'Player not found in this clan': t('error_player_not_in_clan'),
      'player_not_in_clan': t('error_player_not_in_clan'),
      'clan_not_found': t('error_clan_not_found'),
      'insufficient_role': t('error_insufficient_role'),
      'already_pending': t('error_already_pending'),
      'already_registered': t('error_already_registered'),
      'player_tag_taken': t('error_player_tag_taken'),
      'banned': t('error_account_banned'),
    };
    return errorMap[error] || error;
  }, [t]);

  const validatePlayerExists = useCallback(async (tag, cTag) => {
    try {
      // Pass withAuth so backend can check if user already has pending/approved registration
      const response = await apiGet(
        `/v1/auth/validate-player?tag=${encodeURIComponent(tag)}&clan_tag=${encodeURIComponent(cTag)}`,
        { withAuth: true }
      );
      return response;
    } catch (err) {
      return { valid: false, error: translateError(err.message) };
    }
  }, [translateError]);

  const handlePlayerTagChange = (e) => {
    setPlayerTag(e.target.value.toUpperCase());
    setErrors(prev => ({ ...prev, playerTag: null }));
    setValidationStatus(null);
  };

  const handleClanTagChange = (e) => {
    setClanTag(e.target.value.toUpperCase());
    setErrors(prev => ({ ...prev, clanTag: null }));
    setValidationStatus(null);
  };

  const handleMessageChange = (e) => {
    // Sanitize input to prevent XSS while allowing normal text including spaces
    const sanitized = DOMPurify.sanitize(e.target.value, {
      ALLOWED_TAGS: [], // No HTML tags allowed
      ALLOWED_ATTR: [], // No attributes allowed
      KEEP_CONTENT: true // Keep the text content
    });
    setMessage(sanitized);
    setErrors(prev => ({ ...prev, message: null }));
  };

  const validateStep1 = async () => {
    const newErrors = {};
    if (!playerTag) newErrors.playerTag = t('error_tag_required');
    else if (!validatePlayerTag(playerTag)) newErrors.playerTag = t('error_tag_format');
    if (!clanTag) newErrors.clanTag = t('error_clan_required');
    else if (!validatePlayerTag(clanTag)) newErrors.clanTag = t('error_tag_format');

    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return false; }

    setLoading(true);
    const result = await validatePlayerExists(playerTag, clanTag);
    setLoading(false);

    if (!result?.valid) {
      setErrors({ playerTag: translateError(result?.error) || t('error_player_not_found') });
      setValidationStatus('error');
      return false;
    }
    // Store player name and clan name from backend response
    setPlayerName(result.player_name || '');
    setClanName(result.clan_name || '');
    setClanBadgeUrl(result.clan_badge_url || '');
    setValidationStatus('success');
    return true;
  };

  const handleNext = async () => {
    if (step === 1) {
      const valid = await validateStep1();
      if (valid) setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(s => s - 1);
    setSubmitError('');
  };

  // post-login flow: submit directly (user is already authenticated)
  const handleSubmit = async () => {
    if (message.length > 200) { setErrors({ message: t('error_message_too_long') }); return; }
    setLoading(true);
    setSubmitError('');
    try {
      const result = await apiPost('/v1/auth/register', {
        player_tag: playerTag,
        clan_tag: clanTag,
        message: message
      }, { withAuth: true });

      if (result.success) {
        await refreshProfile();
        setRegistrationComplete(true);
      } else {
        setSubmitError(translateError(result.error) || t('error_registration_failed'));
      }
    } catch (err) {
      setSubmitError(translateError(err.message) || t('error_registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  // pre-login flow step 3: sign in with Discord (registration stored in sessionStorage, submitted after login)
  const handleSignInToComplete = async () => {
    // Store pending registration in sessionStorage so it can be submitted after OAuth redirect
    sessionStorage.setItem('pending_registration', JSON.stringify({ playerTag, clanTag, message }));
    try { await loginWithDiscord(); } catch {}
  };

  // Handle close: in post-login mode closing logs the user out
  const handleClose = async () => {
    if (mode === 'post-login') {
      await logout();
    }
    onClose();
  };

  const stepDots = Array.from({ length: totalSteps }, (_, i) => i + 1);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{t('onboarding_title')}</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {stepDots.map(s => (
                <span key={s} className={`w-2 h-2 rounded-full ${step === s ? 'bg-blue-500' : step > s ? 'bg-blue-800' : 'bg-gray-600'}`} />
              ))}
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* post-login banner: account not linked */}
        {mode === 'post-login' && step === 1 && !registrationComplete && (
          <div className="mb-4 bg-yellow-900/30 border border-yellow-600 rounded-lg p-3 flex gap-2">
            <span className="text-yellow-400 flex-shrink-0">⚠️</span>
            <p className="text-yellow-300 text-sm">{t('onboarding_not_linked_warning')}</p>
          </div>
        )}

        {/* STEP 1 — player tag + clan tag */}
        {step === 1 && !registrationComplete && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">{t('onboarding_step1_desc')}</p>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('label_player_tag')}</label>
              <div className="relative">
                <input
                  type="text"
                  value={playerTag}
                  onChange={handlePlayerTagChange}
                  placeholder="#ABC123"
                  maxLength={10}
                  className={`w-full px-4 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.playerTag ? 'border-red-500' : validationStatus === 'success' ? 'border-green-500' : 'border-gray-600'
                  }`}
                />
                {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><LoadingSpinner /></div>}
                {validationStatus === 'success' && !loading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
              {errors.playerTag && <p className="mt-1 text-sm text-red-400">{errors.playerTag}</p>}
              <p className="mt-1 text-xs text-gray-500">{t('hint_player_tag')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('label_clan_tag')}</label>
              <input
                type="text"
                value={clanTag}
                onChange={handleClanTagChange}
                placeholder="#ABC123"
                maxLength={10}
                className={`w-full px-4 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.clanTag ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.clanTag && <p className="mt-1 text-sm text-red-400">{errors.clanTag}</p>}
              <p className="mt-1 text-xs text-gray-500">{t('hint_clan_tag')}</p>
            </div>

            <button
              onClick={handleNext}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <LoadingSpinner /> : t('button_continue')}
            </button>
          </div>
        )}

        {/* STEP 2 — optional message + submit (post-login) OR continue (pre-login) */}
        {step === 2 && !registrationComplete && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">{t('onboarding_step2_desc')}</p>

            <div className="bg-gray-700 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 text-sm">{t('label_player')}:</span>
                <div className="text-right">
                  <div className="text-white font-medium">{playerName || playerTag}</div>
                  <div className="text-xs text-gray-500">{playerTag}</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 text-sm">{t('label_clan')}:</span>
                <div className="text-right flex items-center gap-2">
                  {clanBadgeUrl && <img src={clanBadgeUrl} alt="" className="w-6 h-6" />}
                  <div>
                    <div className="text-white font-medium">{clanName || clanTag}</div>
                    <div className="text-xs text-gray-500">{clanTag}</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('label_optional_message')}</label>
              <textarea
                value={message}
                onChange={handleMessageChange}
                placeholder={t('placeholder_message')}
                maxLength={200}
                rows={3}
                className={`w-full px-4 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                  errors.message ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              <div className="flex justify-between mt-1">
                {errors.message ? <p className="text-sm text-red-400">{errors.message}</p> : <span />}
                <span className="text-xs text-gray-500">{message.length}/200</span>
              </div>
            </div>

            {submitError && (
              <div className="p-3 bg-red-900 bg-opacity-50 border border-red-700 rounded-lg">
                <p className="text-sm text-red-300">{submitError}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleBack} disabled={loading} className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors">
                {t('button_back')}
              </button>
              {mode === 'post-login' ? (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <LoadingSpinner /> : t('button_submit_request')}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  {t('button_continue')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 — pre-login only: sign in with Discord to complete */}
        {step === 3 && mode === 'pre-login' && (
          <div className="space-y-4">
            <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4 text-center">
              <p className="text-blue-300 text-sm mb-1 font-medium">{t('onboarding_step3_title')}</p>
              <p className="text-gray-400 text-xs">{t('onboarding_step3_desc')}</p>
            </div>

            <div className="bg-gray-700 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 text-sm">{t('label_player')}:</span>
                <div className="text-right">
                  <div className="text-white font-medium">{playerName || playerTag}</div>
                  <div className="text-xs text-gray-500">{playerTag}</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 text-sm">{t('label_clan')}:</span>
                <div className="text-right flex items-center gap-2">
                  {clanBadgeUrl && <img src={clanBadgeUrl} alt="" className="w-6 h-6" />}
                  <div>
                    <div className="text-white font-medium">{clanName || clanTag}</div>
                    <div className="text-xs text-gray-500">{clanTag}</div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleBack}
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              ← {t('button_back')}
            </button>

            <button
              onClick={handleSignInToComplete}
              className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <DiscordIcon style={{ width: '1.1rem', height: '1.1rem' }} />
              {t('onboarding_signin_discord')}
            </button>
          </div>
        )}

        {/* SUCCESS STEP — registration complete */}
        {registrationComplete && (
          <div className="fixed inset-0 bg-gray-800 rounded-xl z-10 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-16 h-16 bg-green-900/50 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{t('registration_success_title')}</h3>
            <p className="text-gray-300 mb-6">{t('registration_success_message')}</p>
            <button
              onClick={onComplete}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('button_done')}
            </button>
          </div>
        )}

        <p className="mt-4 text-xs text-gray-500 text-center">{t('onboarding_footer')}</p>
      </div>
    </div>
  );
}
